import type { LyricsEntry, LyricsRowData } from '@/data/Lyrics';
import Lyrics from '@/data/Lyrics';
import type { MetadataFormat } from '@/data/Metadata';
import type { Pixel, Unit } from '@/data';
import type { Image3DPageData } from '@/data/Source';
import MetadataParser from '../MetadataParser';
import type { ImageDimensions } from './SourceParser';
import UnitParser from '../UnitParser';
import type { Image3DSyncFormat } from '../UnitParser';

export interface LyricsFileFormat extends MetadataFormat{
  lyrics?: string;
  id?: string;
  name?: string;
}

export function lyricsFormatFromNewFormat(sourceFormat: Map<string, unknown>): LyricsFileFormat {
  const refs: Record<string, string> = {};
  (Array.from(sourceFormat.keys()) as string[]).filter((key) => key.startsWith('ref')).forEach((refKey) => { refs[refKey] = sourceFormat.get(refKey) as string; });

  return {
    lyrics: sourceFormat.get('lyrics') as string,
    info: sourceFormat.get('text') as string,
    license: sourceFormat.get('license') as string,
    contributors: sourceFormat.get('contributors') as Record<string, string>,
    refs,
    id: sourceFormat.get('id') as string,
    name: sourceFormat.get('name') as string,
    year: sourceFormat.get('year') as string,
  };
}

export default class LyricsParser extends Lyrics {
  private static createDefaultPixelUnit(): Unit<Pixel> {
    const defaultPositions: Image3DSyncFormat = {
      pages: [{
        image: '',
        rows: [{
          'onset-x': [{ onset: 0, x: 0 as Pixel }, { onset: 1, x: 2 as Pixel }],
          'start-x': 0 as Pixel,
          'end-x': 1600 as Pixel,
          y: 0 as Pixel,
          height: 100,
          staves: [],
        }],
      }],
    };
    return UnitParser.createImage3DUnit(defaultPositions);
  }

  private static createDefaultDimensions(): ImageDimensions {
    return { width: 1600 as Pixel, height: 100 as Pixel };
  }

  private static createDefaultPages(): Image3DPageData[] {
    return [{
      image: '',
      height: 100 as Pixel,
      width: 1600 as Pixel,
      rows: [{
        staves: [],
        y: 0 as Pixel,
        height: 100 as Pixel,
        start: 0 as Pixel,
        end: 1600 as Pixel,
      }],
    }];
  }

  static fromFormat(
    format: LyricsFileFormat,
    lyricsEntries: LyricsEntry[],
    pixelUnit?: Unit<Pixel>,
    dim?: ImageDimensions,
    pages?: Image3DPageData[],
    renderRows: LyricsRowData[] = [],
  ): Lyrics {
    if (!lyricsEntries || lyricsEntries.length === 0) {
      throw new Error('No lyrics entries provided for parsing');
    }

    const metadata = MetadataParser.fromFormat(format);

    // will be overwritten in the vue
    const tempPixelUnit = pixelUnit ?? this.createDefaultPixelUnit();
    const tempDim = dim ?? this.createDefaultDimensions();
    const tempPageData = pages ?? this.createDefaultPages();

    return new Lyrics({
      lyricsEntries,
      pixelUnit: tempPixelUnit,
      height: tempDim.height,
      width: tempDim.width,
      pages: tempPageData,
      metadata,
      name: format.name,
      renderRows,
      isLinear: true,
    });
  }
}
